import type { MonitoringConfig } from './core/FactoryMonitoringClient'
import { FactoryMonitoringClient } from './core/FactoryMonitoringClient'
import type {
  CustomMetricEntry,
  ILogger,
  IMetrics,
  MetricEntry,
  MonitoringClient,
  RequestLike,
} from './core/MonitoringClient'
import { NextJSMiddleware } from './middleware/NextJSMiddleware'

// Default configuration
const defaultConfig: MonitoringConfig = {
  endpoint: '/api/monitor',
  maxBatchSize: 50,
  flushInterval: 10000,
  maxPayloadSize: 60000,
}

// Create the factory instance
const factory = FactoryMonitoringClient.create(defaultConfig)
let clientPromise: Promise<MonitoringClient> | null = null

// Initialize client lazily
const getClient = () => {
  if (!clientPromise) {
    clientPromise = factory.client()
  }
  return clientPromise
}

// Logger implementation
export const log: ILogger = {
  async info({
    message,
    metadata,
    request,
  }: {
    message: string
    metadata?: Record<string, unknown>
    request?: RequestLike
  }) {
    try {
      const client = await getClient()
      client.info({ message, metadata, request })
    } catch (error) {
      // Silently fail to prevent app crashes
    }
  },

  async warn({
    message,
    metadata,
    request,
  }: {
    message: string
    metadata?: Record<string, unknown>
    request?: RequestLike
  }) {
    try {
      const client = await getClient()
      client.warn({ message, metadata, request })
    } catch (error) {
      // Silently fail to prevent app crashes
    }
  },

  async error({
    message,
    metadata,
    request,
  }: {
    message: string
    metadata?: Record<string, unknown>
    request?: RequestLike
  }) {
    try {
      const client = await getClient()
      client.error({ message, metadata, request })
    } catch (error) {
      // Silently fail to prevent app crashes
    }
  },

  async debug({
    message,
    metadata,
    request,
  }: {
    message: string
    metadata?: Record<string, unknown>
    request?: RequestLike
  }) {
    try {
      const client = await getClient()
      client.debug({ message, metadata, request })
    } catch (error) {
      // Silently fail to prevent app crashes
    }
  },
}

// Metrics implementation
export const metric: IMetrics = {
  async send(metricEntry: MetricEntry | CustomMetricEntry) {
    try {
      const client = await getClient()
      client.sendMetric(metricEntry)
    } catch (error) {
      // Silently fail to prevent app crashes
    }
  },
}

// Middleware exports
export const middleware = {
  nextjs: NextJSMiddleware?.create(),
}

// Export types and enums for consumers
export { LogLevel } from './core/MonitoringClient'
export type { LogEntry, MetricEntry, UnifiedPayload } from './core/MonitoringClient'
